// SVG icon constant to avoid repetition
const CART_ICON_SVG = `<svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="9" cy="21" r="1"></circle>
  <circle cx="20" cy="21" r="1"></circle>
  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
</svg>`;

// Global cart state management
const CartState = {
  cache: null,
  pending: null,
  listeners: new Set(),

  async fetch(force = false) {
    if (!force && this.cache) return this.cache;
    if (!force && this.pending) return this.pending;

    const request = (async () => {
      try {
        const res = await fetch("/cart.js");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        this.cache = data;
        return data;
      } catch (err) {
        console.error('Error fetching cart:', err);
        return { items: [], item_count: 0, total_price: 0 };
      } finally {
        if (this.pending === request) this.pending = null;
      }
    })();

    this.pending = request;
    return request;
  },
  
  async update(force = true) {
    const cart = await this.fetch(force);
    this.notify(cart);
    return cart;
  },
  
  notify(cart) {
    this.listeners.forEach(callback => callback(cart));
  },
  
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
};

async function initCartForms(scope = document) {
  const productForms = scope.querySelectorAll(".new-product-form");

  function parsePriceParam(value) {
    if (value === null || value === undefined || value === "") return null;
    const normalized = String(value).replace(/,/g, "").trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getActivePriceRange() {
    const params = new URLSearchParams(window.location.search);
    return {
      min: parsePriceParam(params.get("filter.v.price.gte")),
      max: parsePriceParam(params.get("filter.v.price.lte")),
    };
  }

  function variantMatchesPriceRange(variant, range) {
    if (!variant || !range) return false;
    if (range.min === null && range.max === null) return false;
    const price = variant.price / 100;
    if (range.min !== null && price < range.min) return false;
    if (range.max !== null && price > range.max) return false;
    return true;
  }

  async function getCart(force = false) {
    return CartState.fetch(force);
  }

  function showCartToast(message = "Added to Cart!") {
    let toast = document.querySelector(".cart-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "cart-toast";
      document.body.appendChild(toast);
    }

    toast.textContent = message;

    // retrigger animation
    toast.classList.remove("show");
    void toast.offsetWidth; // force reflow
    toast.classList.add("show");

    setTimeout(() => {
      toast.classList.remove("show");

      // remove from DOM after transition ends
      setTimeout(() => {
        if (toast) toast.remove();
      }, 300);
    }, 2500);
  }

  function updateCartBubble(count) {
    const bubble = document.querySelector(".header__icon--cart .cart-count-bubble");
    if (bubble) {
      bubble.textContent = count;
    } else {
      const cartIcon = document.querySelector(".header__icon--cart");
      if (cartIcon) {
        const newBubble = document.createElement("div");
        newBubble.className = "cart-count-bubble flo-cart-icon-bubble";
        newBubble.textContent = count;
        cartIcon.appendChild(newBubble);
      }
    }
  }
  
  function notifyCartUpdate(cart) {
    // Notify Shopflo
    if (window.Shopflo && typeof window.Shopflo.refreshCart === 'function') {
      window.Shopflo.refreshCart();
    }
    
    // Trigger custom event for third-party apps
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { cart: cart } 
    }));
    
    // Update global cart state
    CartState.cache = cart;
    CartState.notify(cart);
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  // -------- CUSTOM QUANTITY (card only) --------
  function showQuantityInput(container, variantId, addBtn, quantity = 1) {
    const oldQty = container.querySelector(".card-quantity");
    if (oldQty) oldQty.remove();

    const qtyEl = document.createElement("div");
    qtyEl.className = "card-quantity";
    qtyEl.innerHTML = `
        <button class="quantity__button" name="minus" type="button">-</button>
        <input class="quantity__input" type="number" min="0" value="${quantity}">
        <button class="quantity__button" name="plus" type="button">+</button>
      `;

    addBtn.insertAdjacentElement("afterend", qtyEl);

    const minusBtn = qtyEl.querySelector('[name="minus"]');
    const plusBtn = qtyEl.querySelector('[name="plus"]');
    const input = qtyEl.querySelector(".quantity__input");

    async function updateCartQuantity(newQty) {
      if (newQty < 0) newQty = 0;
      try {
        const res = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ updates: { [variantId]: newQty } }),
        });
        const cart = await res.json();
        input.value = newQty;
        updateCartBubble(cart.item_count);
        notifyCartUpdate(cart);

        if (newQty <= 0) {
          qtyEl.remove();
          if (addBtn) addBtn.style.display = "flex";
        }
      } catch (err) {
        console.error("Cart update error:", err);
      }
    }

    minusBtn.addEventListener("click", () =>
      updateCartQuantity(parseInt(input.value) - 1)
    );
    plusBtn.addEventListener("click", () =>
      updateCartQuantity(parseInt(input.value) + 1)
    );
    input.addEventListener(
      "change",
      debounce(() => updateCartQuantity(parseInt(input.value) || 0), 300)
    );
  }

  // -------- BIND CUSTOM FORMS --------
  productForms.forEach((form) => {
    if (form.dataset.initialized) {
      return;
    }
    form.dataset.initialized = "true";

    const variants = JSON.parse(form.dataset.variants);
    const variantInput = form.querySelector("input[name='id'], select[name='id']");
    const selectInput = form.querySelector("select[name='id']");
    const addBtn = form.querySelector(".atc-btn");
    const variantTabs = form.querySelectorAll(".variant-tab");
    const sectionId = form.dataset.sectionId;
    const productId = form.dataset.productId;
    const isReelForm = form.classList.contains("reel-product-form");
    const selectWrap = form.querySelector(".reel-variant-select");
    
    let productContainer = form.querySelector(`#CardInfo-${sectionId}-${productId}`);
    if (!productContainer) {
      productContainer = form.parentElement?.querySelector(`#CardInfo-${sectionId}-${productId}`);
    }
    if (!productContainer) {
      productContainer = document.querySelector(`#CardInfo-${sectionId}-${productId}`);
    }
    if (!productContainer && isReelForm) {
      productContainer = form.closest(".reel-product-card");
    }
    
    const regularPriceEl =
      productContainer?.querySelector(".product-price .price-text") ||
      productContainer?.querySelector(".reel-product-price .price");
    const salePriceEl =
      productContainer?.querySelector(".compare-product-price .price-text") ||
      productContainer?.querySelector(".reel-product-price .compare-price");

    let selectedOptions = [];
    const activePriceRange = getActivePriceRange();

    function updateVariant(variant) {
      if (variant) {
        if (variantInput) {
          variantInput.value = variant.id;
        }
        addBtn.disabled = !variant.available;
        if (isReelForm) {
          addBtn.innerHTML = variant.available ? "Add to cart" : "Sold out";
        } else {
          addBtn.innerHTML = variant.available ? `${CART_ICON_SVG} Add to cart` : "Sold out";
        }
        if (regularPriceEl) {
          regularPriceEl.textContent = (variant.price / 100).toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          });
        }
        if (salePriceEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
          salePriceEl.textContent = (variant.compare_at_price / 100).toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          });
        } else if (salePriceEl) {
          salePriceEl.textContent = "";
        }
      }
    }

    // Init variant
    const currentVariantFromInput = variants.find((v) => v.id == variantInput?.value) || variants[0];
    const matchedPriceVariant = variants.find(
      (variant) => variant.available && variantMatchesPriceRange(variant, activePriceRange)
    );
    const currentVariant = matchedPriceVariant || currentVariantFromInput;
    if (currentVariant) {
      selectedOptions = [...currentVariant.options];
      form.querySelectorAll(".variant-tab").forEach((button) => button.classList.remove("active"));
      currentVariant.options.forEach((opt, i) => {
        form
          .querySelector(
            `.variant-tab[data-option-index="${i + 1}"][data-value="${opt}"]`
          )
          ?.classList.add("active");
      });
      updateVariant(currentVariant);
    }

    // Variant switch
    variantTabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        if (tab.classList.contains("disabled")) return;
        const optionIndex = parseInt(tab.dataset.optionIndex) - 1;
        selectedOptions[optionIndex] = tab.dataset.value;

        tab.parentElement.querySelectorAll(".variant-tab").forEach((sib) => sib.classList.remove("active"));
        tab.classList.add("active");

        const matchedVariant = variants.find((v) =>
          v.options.every((opt, i) => selectedOptions[i] === opt)
        );
        updateVariant(matchedVariant);

        if (matchedVariant) {
          syncFormWithCart(matchedVariant.id);
        }
      });
    });

    // Custom variant dropdown (product card)
    const variantDropdown = form.querySelector("[data-variant-dropdown]");
    if (variantDropdown) {
      const dropdownToggle = variantDropdown.querySelector("[data-dropdown-toggle]");
      const dropdownLabel = variantDropdown.querySelector(".variant-dropdown__label");
      const dropdownListEl = variantDropdown.querySelector(".variant-dropdown__list");
      const dropdownOptions = variantDropdown.querySelectorAll(".variant-dropdown__option");
      // While open the list is moved to <body> with position:fixed so no
      // ancestor overflow can clip it and no sibling card can paint over it.
      const listHome = dropdownListEl ? dropdownListEl.parentNode : null;
      const listAnchor = dropdownListEl ? dropdownListEl.nextSibling : null;
      let listPortaled = false;

      // Top edge the list must not spill past: the nearest ancestor that
      // actually scrolls/clips vertically (e.g. the search results panel),
      // otherwise the top of the viewport.
      const getClipTop = () => {
        let top = 0;
        let node = dropdownToggle.parentElement;
        while (node && node !== document.documentElement) {
          const oy = getComputedStyle(node).overflowY;
          const canClip = oy === "auto" || oy === "scroll" || oy === "hidden" || oy === "clip";
          if (canClip && node.scrollHeight > node.clientHeight + 4) {
            top = node.getBoundingClientRect().top;
            break;
          }
          node = node.parentElement;
        }
        // Desktop only: the sticky site header covers the top of the viewport,
        // so an upward-opening list has to clear it. Mobile is left untouched.
        if (window.matchMedia && window.matchMedia("(min-width: 990px)").matches) {
          const header = document.querySelector(".section-header");
          if (header && !header.classList.contains("shopify-section-header-hidden")) {
            const pos = getComputedStyle(header).position;
            if (pos === "sticky" || pos === "fixed") {
              top = Math.max(top, header.getBoundingClientRect().bottom);
            }
          }
        }
        return top;
      };

      // Open the list ABOVE the toggle while the card is still high enough on
      // screen and the list fits in the room there; otherwise open BELOW.
      // Mobile flips when the card top reaches the clip edge; desktop flips
      // only once 95% of the card image has scrolled past it. The list is
      // position:fixed (portaled to <body>) so it's never clipped/covered.
      const placeDropdown = () => {
        if (!dropdownListEl || !dropdownToggle) return;
        dropdownListEl.style.maxHeight = "";

        const r = dropdownToggle.getBoundingClientRect();
        const cardEl = variantDropdown.closest(".product-card") || variantDropdown;
        const cardTop = cardEl.getBoundingClientRect().top;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const clipTop = getClipTop();
        const gap = 4;
        const listHeight = dropdownListEl.scrollHeight;
        const roomAbove = r.top - clipTop - gap;
        const roomBelow = vh - r.bottom - gap;

        // Reference line that must stay below the clip edge to keep opening up.
        let flipLine = cardTop;
        if (window.matchMedia && window.matchMedia("(min-width: 990px)").matches) {
          const imgEl = cardEl.querySelector(".product-image-container, .product-image");
          if (imgEl) {
            const ir = imgEl.getBoundingClientRect();
            flipLine = ir.top + ir.height * 0.95;
          }
        }

        const openUp = flipLine > clipTop && roomAbove >= listHeight;
        const room = Math.max(0, openUp ? roomAbove : roomBelow);

        dropdownListEl.style.position = "fixed";
        dropdownListEl.style.left = r.left + "px";
        dropdownListEl.style.width = r.width + "px";
        dropdownListEl.style.right = "auto";
        dropdownListEl.style.zIndex = "2147483000";
        dropdownListEl.style.maxHeight = Math.max(120, Math.min(280, room)) + "px";
        if (openUp) {
          dropdownListEl.style.top = "auto";
          dropdownListEl.style.bottom = vh - r.top + gap + "px";
        } else {
          dropdownListEl.style.top = r.bottom + gap + "px";
          dropdownListEl.style.bottom = "auto";
        }
      };

      let repositionPending = false;
      const reposition = () => {
        if (!listPortaled || repositionPending) return;
        repositionPending = true;
        requestAnimationFrame(() => {
          repositionPending = false;
          if (listPortaled) placeDropdown();
        });
      };

      const openDropdown = () => {
        if (!dropdownListEl) return;
        // The toggle stops click propagation, so other cards' outside-click
        // handlers never fire — tell them directly so only one stays open.
        document.dispatchEvent(
          new CustomEvent("variant-dropdown:open", { detail: { dropdown: variantDropdown } })
        );
        variantDropdown.classList.add("is-open");
        dropdownToggle?.setAttribute("aria-expanded", "true");
        dropdownListEl.hidden = false;
        document.body.appendChild(dropdownListEl);
        listPortaled = true;
        placeDropdown();
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
      };

      const closeDropdown = () => {
        variantDropdown.classList.remove("is-open");
        dropdownToggle?.setAttribute("aria-expanded", "false");
        window.removeEventListener("scroll", reposition, true);
        window.removeEventListener("resize", reposition);
        if (listPortaled && listHome) {
          if (listAnchor && listAnchor.parentNode === listHome) {
            listHome.insertBefore(dropdownListEl, listAnchor);
          } else {
            listHome.appendChild(dropdownListEl);
          }
          listPortaled = false;
        }
        if (dropdownListEl) {
          dropdownListEl.hidden = true;
          dropdownListEl.removeAttribute("style");
        }
      };

      const syncDropdownLabel = () => {
        const selected =
          dropdownListEl?.querySelector(".variant-dropdown__option.active") || dropdownOptions[0];
        if (selected && dropdownLabel) dropdownLabel.textContent = selected.textContent.trim();
      };

      syncDropdownLabel();

      dropdownToggle?.addEventListener("click", (e) => {
        e.stopPropagation();
        if (variantDropdown.classList.contains("is-open")) {
          closeDropdown();
        } else {
          openDropdown();
        }
      });

      // The .variant-tab click handler above already switches the variant, price
      // and cart state — here we only mirror the choice into the toggle and close.
      dropdownOptions.forEach((option) => {
        option.addEventListener("click", () => {
          if (option.disabled) return;
          syncDropdownLabel();
          closeDropdown();
        });
      });

      document.addEventListener("click", (e) => {
        if (!variantDropdown.contains(e.target) && !dropdownListEl?.contains(e.target)) {
          closeDropdown();
        }
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeDropdown();
      });

      document.addEventListener("variant-dropdown:open", (e) => {
        if (e.detail.dropdown !== variantDropdown && variantDropdown.classList.contains("is-open")) {
          closeDropdown();
        }
      });
    }

    // Variant select (reels)
    if (selectInput) {
      selectInput.addEventListener("change", () => {
        const matchedVariant = variants.find((v) => v.id == selectInput.value);
        updateVariant(matchedVariant);
        if (matchedVariant) {
          syncFormWithCart(matchedVariant.id);
        }
        if (selectWrap && !selectWrap.classList.contains("is-hidden")) {
          addBtn.click();
        }
      });
    }

    // Sync form UI with cart state
    function syncFormWithCart(variantId) {
      const cart = CartState.cache;
      if (!cart) return;
      
      const lineItem = cart.items.find(item => item.variant_id == variantId);
      
      if (lineItem) {
        addBtn.style.display = "none";
        const existingQty = form.querySelector(".card-quantity");
        if (existingQty) {
          const input = existingQty.querySelector(".quantity__input");
          if (input && parseInt(input.value) !== lineItem.quantity) {
            input.value = lineItem.quantity;
          }
        } else {
          showQuantityInput(form, lineItem.variant_id, addBtn, lineItem.quantity);
        }
      } else {
        const oldQty = form.querySelector(".card-quantity");
        if (oldQty) oldQty.remove();
        addBtn.style.display = "flex";
      }
    }

    // Add to cart
    addBtn.addEventListener("click", async (e) => {
      if (isReelForm) {
        e.preventDefault();
      }
      if (selectInput && selectWrap && selectWrap.classList.contains("is-hidden")) {
        selectWrap.classList.remove("is-hidden");
        selectInput.focus();
        return;
      }

      const variantId = variantInput?.value;
      if (!variantId) return alert("Please select a variant first");

      const selectedVariant = variants.find(v => v.id == variantId);
      if (!selectedVariant || !selectedVariant.available) {
        return alert("Please select an available variant");
      }

      addBtn.disabled = true;
      addBtn.textContent = "Adding...";

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const addedItem = await res.json();

        addBtn.disabled = false;
        if (isReelForm) {
          addBtn.innerHTML = "Add to cart";
        } else {
          addBtn.innerHTML = `${CART_ICON_SVG} Add to cart`;
        }
        addBtn.style.display = "none";
        showQuantityInput(form, addedItem.variant_id, addBtn, addedItem.quantity);
        showCartToast("Added to Cart!");
        
        const cart = await CartState.update(true);
        updateCartBubble(cart.item_count);
      } catch (err) {
        console.error(err);
        addBtn.disabled = false;
        if (isReelForm) {
          addBtn.innerHTML = "Add to cart";
        } else {
          addBtn.innerHTML = `${CART_ICON_SVG} Add to cart`;
        }
      }
    });

    // On load: check if already in cart
    getCart().then((cart) => {
      const lineItem = cart.items.find(item => item.variant_id == variantInput?.value);
      if (lineItem) {
        addBtn.style.display = "none";
        showQuantityInput(form, lineItem.variant_id, addBtn, lineItem.quantity);
      }
      updateCartBubble(cart.item_count);
    });

    // Subscribe to cart updates (event-driven)
    const unsubscribe = CartState.subscribe((cart) => {
      const currentVariantId = parseInt(variantInput?.value);
      syncFormWithCart(currentVariantId);
      updateCartBubble(cart.item_count);
    });

    // Cleanup subscription when form is removed
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node === form || node.contains(form)) {
            unsubscribe();
            observer.disconnect();
          }
        });
      });
    });
    
    if (form.parentElement) {
      observer.observe(form.parentElement, { childList: true, subtree: true });
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // Initialize normal product forms immediately
  initCartForms();

  // Use MutationObserver to watch for related products (event-driven)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if the added node is or contains related products grid
          const relatedGrid = node.classList?.contains('related-products') 
            ? node.querySelector('.grid')
            : node.querySelector?.('.related-products .grid');
          
          if (relatedGrid) {
            initCartForms(relatedGrid);
          }
        }
      });
    });
  });

  // Observe the document body for related products being added
  observer.observe(document.body, { 
    childList: true, 
    subtree: true 
  });

  // Check if related products already exist
  const existingRelatedGrid = document.querySelector(".related-products .grid");
  if (existingRelatedGrid) {
    initCartForms(existingRelatedGrid);
  }

  // Handle Shopify section reloads
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    const relatedGrid = section.querySelector(".related-products .grid");
    if (relatedGrid) {
      initCartForms(relatedGrid);
    }
  });

  // Listen for external cart updates (from cart drawer, other scripts, etc.)
  document.addEventListener('cart:refresh', async () => {
    await CartState.update(true);
  });
  
  // Listen for Shopify theme editor events
  document.addEventListener('shopify:block:select', async () => {
    await CartState.update(true);
  });
});



